import type { PublicTrace, PublicTraceEntry } from '@onefootprint/request-types/dashboard';
import { getFiredRuleId } from '@/components/playbook-builder/utils/get-fired-rule-id';

type RulePathStep = {
  id: string;
  kind: 'conditional' | 'rules';
  name?: string;
  /** The expressions of the rules that fired on the path the node took; empty when none did. */
  conditions: string[];
  /** Where the node went: a conditional's branch, or the value a rules node assigned. */
  outcome: string;
  /** The data field a rules node assigns `outcome` to, such as `kyc_result`. */
  field?: string;
  /** A conditional that left by a non-default branch with nothing triggered: a sandbox outcome. */
  isNotEvaluated: boolean;
};

// A conditional or rules node counts only once it executed properly.
const isExecutedRuleEntry = (entry: PublicTraceEntry) =>
  (entry.nodeKind === 'conditional' || entry.nodeKind === 'rules') &&
  (entry.result.signalKind === 'proceed' || entry.result.signalKind === 'halt');

// Two rules can share an expression; listed or counted twice, it reads as one more rule than the reader sees.
const unique = (expressions: string[]) => [...new Set(expressions)];

/** The rule nodes an onboarding went through, in order, with what fired in each. */
const getRulePathSteps = (trace: PublicTrace): RulePathStep[] =>
  trace.entries.filter(isExecutedRuleEntry).flatMap((entry): RulePathStep[] => {
    const { context } = entry;
    const node = trace.graph.nodes.find(({ id }) => id === entry.decisionNodeId);
    if (context?.kind === 'conditional') {
      const conditions = unique(
        context.branchResults
          .filter(({ branchName }) => branchName === context.outcomeBranchName)
          .flatMap(({ results }) => results)
          .filter(({ result }) => result)
          .map(({ ruleExpression }) => ruleExpression),
      );
      const defaultBranch = node?.kind === 'conditional' ? node.defaultBranch?.name : undefined;
      return [
        {
          id: entry.decisionNodeId,
          kind: 'conditional',
          name: node?.kind === 'conditional' ? node.name : undefined,
          conditions,
          outcome: context.outcomeBranchName,
          isNotEvaluated: !conditions.length && context.outcomeBranchName !== defaultBranch,
        },
      ];
    }
    if (context?.kind === 'rules') {
      const rulesNode = node?.kind === 'rules' ? node : undefined;
      const firedRuleId = getFiredRuleId(context.groupResults);
      const firedGroup = rulesNode?.groups.find(group => group.conditions.some(({ id }) => id === firedRuleId));
      const conditions = unique(
        (firedGroup?.conditions ?? [])
          .filter(({ id }) => context.groupResults.find(({ ruleId }) => ruleId === id)?.result)
          .map(({ expression }) => expression),
      );
      const outcome = firedRuleId ? firedGroup?.value : rulesNode?.defaultValue;
      return [
        {
          id: entry.decisionNodeId,
          kind: 'rules',
          name: rulesNode?.name,
          conditions,
          outcome: outcome ?? '',
          field: rulesNode?.key,
          isNotEvaluated: false,
        },
      ];
    }
    return [];
  });

export type { RulePathStep };
export default getRulePathSteps;
